import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';

import { RigsService } from 'src/app/services/rigs.service';
import { ProjectsService } from 'src/app/services/projects.service';
import { Rig, RigSummary } from '@models/rig.interface';
import { LookupDto } from '@models/lookup-dto.interface';

import { RigListComponent } from './rig-list/rig-list.component';
import { RigDetailComponent, RigSavedEvent } from './rig-detail/rig-detail.component';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '@shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-rigs-page',
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinnerModule,
    RigListComponent,
    RigDetailComponent
  ],
  templateUrl: './rigs-page.component.html',
  styleUrl: './rigs-page.component.scss'
})
export class RigsPageComponent implements OnInit {
  private readonly rigsService = inject(RigsService);
  private readonly projectsService = inject(ProjectsService);
  private readonly dialog = inject(MatDialog);

  protected rigs = signal<RigSummary[]>([]);
  protected selectedRig = signal<Rig | null>(null);
  protected selectedId = signal<number | null>(null);
  protected isLoadingList = signal(false);
  protected isLoadingDetail = signal(false);

  protected businessUnits = signal<LookupDto[]>([]);
  protected isLoadingLookups = signal(true);

  ngOnInit(): void {
    this.loadList();
    this.loadLookups();
  }

  private loadLookups(): void {
    this.isLoadingLookups.set(true);
    this.projectsService.getLookups().subscribe({
      next: (lookups) => {
        this.businessUnits.set(lookups.businessUnits);
        this.isLoadingLookups.set(false);
      },
      error: () => this.isLoadingLookups.set(false)
    });
  }

  private loadList(selectId?: number, forceRefresh = false): void {
    this.isLoadingList.set(true);
    this.rigsService.getList(forceRefresh).subscribe({
      next: list => {
        this.rigs.set(list);
        this.isLoadingList.set(false);

        // Re-select after refresh, or restore previous selection
        const targetId = selectId ?? this.selectedId();
        if (targetId !== null) {
          const stillPresent = list.find(r => r.id === targetId);
          if (stillPresent) {
            this.loadDetail(targetId, forceRefresh && selectId === targetId);
          } else {
            this.selectedRig.set(null);
            this.selectedId.set(null);
          }
        }
      },
      error: () => {
        this.isLoadingList.set(false);
      }
    });
  }

  private loadDetail(id: number, forceRefresh = false): void {
    this.isLoadingDetail.set(true);
    this.selectedId.set(id);
    this.rigsService.getById(id, forceRefresh).subscribe({
      next: rig => {
        this.selectedRig.set(rig);
        this.isLoadingDetail.set(false);
      },
      error: () => {
        this.selectedRig.set(null);
        this.isLoadingDetail.set(false);
      }
    });
  }

  protected onRigSelected(summary: RigSummary): void {
    this.loadDetail(summary.id);
  }

  protected onAddClicked(): void {
    // Create an unsaved "new" rig shell
    const newRig: Rig = {
      id: 0,
      name: '',
      alias: null,
      defaultBusinessUnitId: null,
      createdOn: new Date().toISOString()
    };
    this.selectedRig.set(newRig);
    this.selectedId.set(null);
  }

  protected onRigSaved(event: RigSavedEvent): void {
    // Reload the list and re-select the saved rig
    this.selectedId.set(event.id);
    this.loadList(event.id, true);
  }

  protected onRigDeleted(rigId: number): void {
    const dialogRef = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(
      ConfirmationDialogComponent,
      {
        data: {
          title: 'Delete Rig',
          primaryText: 'Are you sure you want to delete this rig?',
          confirmText: 'Delete',
        },
        width: '420px',
        disableClose: true,
      }
    );
    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.rigsService.delete(rigId).subscribe({
        next: () => {
          // Clear selection if the deleted rig was selected
          if (this.selectedId() === rigId) {
            this.selectedRig.set(null);
            this.selectedId.set(null);
          }
          // Reload the list
          this.loadList();
        },
        error: () => {
          alert('Failed to delete rig. Please try again.');
        }
      });
    });
  }
}
